import { StudioState, VideoModel, VideoCreatorErrorCode } from '../../types/store';
import { getModelPropertiesByType } from '../../pages/VideoCreator/ModelProperties';
import {
  updateVideoCreatorPrompt,
  updateVideoCreatorModel,
  updateVideoCreatorAspectRatio,
  updateVideoCreatorDuration,
  updateVideoCreatorSound,
  updateVideoCreatorStatus,
  updateVideoCreatorReferenceDeviation,
  updateVideoCreatorErrorCode,
  initializeVideoCreator,
  updateVideoCreatorRestrictions,
  updateVideoCreatorPricePoints,
  updateVideoCreatorLowestPricePoints,
  updateVideoCreatorIsCalculatingCost,
  updateVideoCreatorGenerationCostModel,
  addVideoCreatorPendingGeneration,
  removeVideoCreatorPendingGeneration,
  updateVideoCreatorModelsExpanded,
} from '../../actions/studio/videoCreator';

const defaultVideoCreatorState = {
  prompt: '',
  model: VideoModel.HAILUO,
  aspectRatio: '4:3' as const,
  duration: 6,
  sound: false,
  status: 'idle' as const,
  errorCode: VideoCreatorErrorCode.NO_ERROR,
  restrictions: undefined,
  pointsPackages: [],
  pricePoints: 0,
  lowestPricePoints: 0,
  generationCostModel: undefined as string | undefined,
  isCalculatingCost: false,
  pendingGenerations: [],
  modelsExpanded: false,
};

export const handleUpdateVideoCreatorPrompt = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorPrompt>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    prompt: action.payload.prompt,
  },
});

export const handleUpdateVideoCreatorModel = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorModel>
): StudioState => {
  const newModel = action.payload.model;
  const modelProps = getModelPropertiesByType(newModel);
  let sound = state.videoCreator.sound;
  if (modelProps?.hasSound === 'free' || modelProps?.hasSound === 'no') {
    sound = false;
  }
  return {
    ...state,
    videoCreator: {
      ...state.videoCreator,
      model: newModel,
      sound: sound,
    },
  };
};

export const handleUpdateVideoCreatorAspectRatio = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorAspectRatio>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    aspectRatio: action.payload.aspectRatio,
  },
});

export const handleUpdateVideoCreatorDuration = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorDuration>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    duration: action.payload.duration,
  },
});

export const handleUpdateVideoCreatorSound = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorSound>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    sound: action.payload.sound,
  },
});

export const handleUpdateVideoCreatorStatus = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorStatus>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    status: action.payload.status,
  },
});

export const handleUpdateVideoCreatorReferenceDeviation = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorReferenceDeviation>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    referenceDeviation: action.payload.referenceDeviation,
  },
});

export const handleUpdateVideoCreatorErrorCode = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorErrorCode>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    errorCode: action.payload.errorCode,
  },
});

export const handleResetVideoCreator = (state: StudioState): StudioState => ({
  ...state,
  videoCreator: defaultVideoCreatorState,
});

export const handleInitializeVideoCreator = (
  state: StudioState,
  action: ReturnType<typeof initializeVideoCreator>
): StudioState => {
  const { restrictions, pointsPackages } = action.payload.initData;
  return {
    ...state,
    videoCreator: {
      ...state.videoCreator,
      restrictions,
      pointsPackages: pointsPackages
        ? pointsPackages.map(pkg => ({
            points: pkg.pricePoints,
            price: pkg.priceDollars,
            subproductId: pkg.subproductId,
          }))
        : [],
    },
  };
};

export const handleUpdateVideoCreatorRestrictions = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorRestrictions>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    restrictions: action.payload.restrictions,
  },
});

export const handleUpdateVideoCreatorPricePoints = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorPricePoints>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    pricePoints: action.payload.pricePoints,
  },
});

export const handleUpdateVideoCreatorLowestPricePoints = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorLowestPricePoints>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    lowestPricePoints: action.payload.lowestPricePoints,
  },
});

export const handleUpdateVideoCreatorIsCalculatingCost = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorIsCalculatingCost>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    isCalculatingCost: action.payload.isCalculatingCost,
  },
});

export const handleUpdateVideoCreatorGenerationCostModel = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorGenerationCostModel>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    generationCostModel: action.payload.modelId,
  },
});

export const handleAddVideoCreatorPendingGeneration = (
  state: StudioState,
  action: ReturnType<typeof addVideoCreatorPendingGeneration>
): StudioState => {
  const { pendingGeneration } = action.payload;
  const currentGenerations = state.videoCreator.pendingGenerations ?? [];
  if (currentGenerations.some(gen => gen.generationId === pendingGeneration.generationId)) {
    return state;
  }
  return {
    ...state,
    videoCreator: {
      ...state.videoCreator,
      pendingGenerations: [...currentGenerations, pendingGeneration],
    },
  };
};

export const handleRemoveVideoCreatorPendingGeneration = (
  state: StudioState,
  action: ReturnType<typeof removeVideoCreatorPendingGeneration>
): StudioState => {
  const { generationId } = action.payload;
  const currentGenerations = state.videoCreator.pendingGenerations ?? [];
  return {
    ...state,
    videoCreator: {
      ...state.videoCreator,
      pendingGenerations: currentGenerations.filter(gen => gen.generationId !== generationId),
    },
  };
};

export const handleUpdateVideoCreatorModelsExpanded = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorModelsExpanded>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    modelsExpanded: action.payload.modelsExpanded,
  },
});
